import React, { useState } from 'react';
import { cn } from '../../../utils/cn';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';

const PaymentMethods = () => {
  const [paymentMethods, setPaymentMethods] = useState([
    {
      id: 1,
      type: 'card',
      isDefault: true,
      brand: 'Visa',
      last4: '4242',
      expiryMonth: '12',
      expiryYear: '2027',
      holderName: 'Sarah Johnson'
    },
    {
      id: 2,
      type: 'card',
      isDefault: false,
      brand: 'Mastercard',
      last4: '8888',
      expiryMonth: '09',
      expiryYear: '2026',
      holderName: 'Sarah Johnson'
    }
  ]);

  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    cardNumber: '',
    holderName: '',
    expiryMonth: '',
    expiryYear: '',
    cvv: ''
  });

  const getCardBrandIcon = (brand) => {
    switch (brand?.toLowerCase()) {
      case 'visa':
        return 'CreditCard';
      case 'mastercard':
        return 'CreditCard';
      case 'amex':
        return 'CreditCard';
      default:
        return 'CreditCard';
    }
  };

  const getCardBrandColor = (brand) => {
    switch (brand?.toLowerCase()) {
      case 'visa':
        return 'text-blue-600';
      case 'mastercard':
        return 'text-red-600';
      case 'amex':
        return 'text-green-600';
      default:
        return 'text-gray-600';
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddPaymentMethod = () => {
    setFormData({
      cardNumber: '',
      holderName: '',
      expiryMonth: '',
      expiryYear: '',
      cvv: ''
    });
    setEditingId(null);
    setShowAddForm(true);
  };

  const handleSavePaymentMethod = () => {
    // Simulate card processing
    const newMethod = {
      id: Math.max(...paymentMethods?.map(p => p?.id)) + 1,
      type: 'card',
      isDefault: paymentMethods?.length === 0,
      brand: 'Visa', // Would be detected from card number
      last4: formData?.cardNumber?.slice(-4),
      expiryMonth: formData?.expiryMonth,
      expiryYear: formData?.expiryYear,
      holderName: formData?.holderName
    };
    
    setPaymentMethods(prev => [...prev, newMethod]);
    setShowAddForm(false);
  };

  const handleDeletePaymentMethod = (id) => {
    setPaymentMethods(prev => prev?.filter(method => method?.id !== id));
  };

  const handleSetDefault = (id) => {
    setPaymentMethods(prev => prev?.map(method => ({
      ...method,
      isDefault: method?.id === id
    })));
  };

  const months = [
    '01', '02', '03', '04', '05', '06',
    '07', '08', '09', '10', '11', '12'
  ];

  const currentYear = new Date()?.getFullYear();
  const years = Array.from({ length: 10 }, (_, i) => (currentYear + i)?.toString());

  return (
    <div className="max-w-4xl">
      <div className="bg-white rounded-lg border border-border overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-border">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-foreground">
                Payment Methods
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                Manage your saved payment methods and billing information
              </p>
            </div>
            <Button
              onClick={handleAddPaymentMethod}
              iconName="Plus"
              iconPosition="left"
            >
              Add Payment Method
            </Button>
          </div>
        </div>

        {/* Add Payment Form */}
        {showAddForm && (
          <div className="px-6 py-6 bg-accent/10 border-b border-border">
            <div className="max-w-xl">
              <h3 className="text-lg font-medium text-foreground mb-4">
                Add New Payment Method
              </h3>

              <div className="space-y-4">
                {/* Card Number */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Card Number
                  </label>
                  <Input
                    value={formData?.cardNumber}
                    onChange={(e) => handleInputChange('cardNumber', e?.target?.value)}
                    placeholder="1234 5678 9012 3456"
                    maxLength={19}
                  />
                </div>

                {/* Cardholder Name */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Cardholder Name
                  </label>
                  <Input
                    value={formData?.holderName}
                    onChange={(e) => handleInputChange('holderName', e?.target?.value)}
                    placeholder="Enter name as shown on card"
                  />
                </div>

                {/* Expiry and CVV */}
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Month
                    </label>
                    <select
                      value={formData?.expiryMonth}
                      onChange={(e) => handleInputChange('expiryMonth', e?.target?.value)}
                      className="w-full px-3 py-2 border border-input rounded-md text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    >
                      <option value="">MM</option>
                      {months?.map(month => (
                        <option key={month} value={month}>{month}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Year
                    </label>
                    <select
                      value={formData?.expiryYear}
                      onChange={(e) => handleInputChange('expiryYear', e?.target?.value)}
                      className="w-full px-3 py-2 border border-input rounded-md text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    >
                      <option value="">YYYY</option>
                      {years?.map(year => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      CVV
                    </label>
                    <Input
                      value={formData?.cvv}
                      onChange={(e) => handleInputChange('cvv', e?.target?.value)}
                      placeholder="123"
                      maxLength={4}
                    />
                  </div>
                </div>

                {/* Security Notice */}
                <div className="flex items-start space-x-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <Icon name="Shield" size={16} className="text-blue-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-blue-800 font-medium">
                      Your payment information is secure
                    </p>
                    <p className="text-xs text-blue-700 mt-1">
                      We use industry-standard encryption to protect your data. Your full card number is never stored on our servers.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end space-x-3 mt-6">
                <Button
                  variant="ghost"
                  onClick={() => setShowAddForm(false)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSavePaymentMethod}
                  iconName="Check"
                  iconPosition="left"
                >
                  Add Payment Method
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Payment Methods List */}
        <div className="p-6">
          {paymentMethods?.length > 0 ? (
            <div className="space-y-4">
              {paymentMethods?.map((method) => (
                <div
                  key={method?.id}
                  className={cn(
                    "p-4 rounded-lg border transition-colors",
                    method?.isDefault 
                      ? "border-primary bg-primary/5" :"border-border hover:border-primary/50"
                  )}
                >
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                    <div className="flex items-center space-x-4 min-w-0 flex-1">
                      {/* Card Icon */}
                      <div className={cn(
                        "w-12 h-8 rounded border-2 flex items-center justify-center flex-shrink-0",
                        getCardBrandColor(method?.brand)
                      )}>
                        <Icon 
                          name={getCardBrandIcon(method?.brand)} 
                          size={20}
                        />
                      </div>

                      {/* Card Details */}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center space-x-2 mb-1 flex-wrap">
                          <span className="font-medium text-foreground whitespace-nowrap">
                            {method?.brand}
                          </span>
                          <span className="text-muted-foreground whitespace-nowrap">
                            •••• {method?.last4}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground whitespace-nowrap">
                          Expires {method?.expiryMonth}/{method?.expiryYear}
                        </p>
                        <p className="text-sm text-muted-foreground truncate">
                          {method?.holderName}
                        </p>
                      </div>
                    </div>

                    {/* Actions - Restructured for proper alignment */}
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 flex-shrink-0">
                      {/* Default Badge or Set Default Button - Same position */}
                      <div className="min-w-[120px] flex justify-end">
                        {method?.isDefault ? (
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-primary text-primary-foreground whitespace-nowrap">
                            Default
                          </span>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleSetDefault(method?.id)}
                            className="whitespace-nowrap text-sm"
                          >
                            Set as Default
                          </Button>
                        )}
                      </div>

                      {/* Remove Button - Always aligned */}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeletePaymentMethod(method?.id)}
                        iconName="Trash"
                        className="text-red-600 hover:text-red-700 whitespace-nowrap text-sm"
                      >
                        Remove
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Icon name="CreditCard" size={48} className="mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">
                No payment methods saved
              </h3>
              <p className="text-sm text-muted-foreground mb-6">
                Add a payment method to make checkout faster and more convenient
              </p>
              <Button onClick={handleAddPaymentMethod}>
                Add Your First Payment Method
              </Button>
            </div>
          )}
        </div>

        {/* Billing Section */}
        {paymentMethods?.length > 0 && (
          <div className="px-6 pb-6">
            <div className="border-t border-border pt-6">
              <h3 className="text-lg font-medium text-foreground mb-4">
                Billing Settings
              </h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 border border-border rounded-lg">
                  <div>
                    <p className="font-medium text-foreground">Auto-renewal</p>
                    <p className="text-sm text-muted-foreground">
                      Automatically renew subscriptions using your default payment method
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" defaultChecked className="sr-only peer" />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                  </label>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentMethods;